/**
 * The templates a new organisation starts with: PRD 8.2 and 8.3 (onboarding),
 * 9.2 and 9.5 (offboarding) and 9.3 (involuntary), written down as data.
 *
 * They are the PRD's tables made executable, and that turned out to be a
 * review of the tables: several places where the PRD reads naturally as prose
 * and does not work as a graph are listed in docs/utuwrk-brief.md under "What
 * writing the starter templates found". Where the PRD was ambiguous these
 * pick one reading and the brief says which.
 *
 * Where the PRD names two owners ("IT + Facilities", "HR + Manager",
 * "HR / Finance") the first does the work and the second signs off.
 *
 * Vendor-neutral on purpose (PRD 5.2): the PRD's own cross-link example is a
 * Microsoft licence tier, which is exactly the kind of thing a Google
 * Workspace customer reads as "not for us". It is an account tier here.
 */

import { COBRA_DESCRIPTION, hard, notFor, onlyFor, soft, triggerIs, triggerIsNot } from "./build.js";
import type { Template, TemplateNode } from "./template.js";

export const EMPLOYEE_TYPES = ["office", "warehouse", "remote", "executive", "contractor"] as const;
export const EMPLOYMENT_STATUSES = ["full-time", "part-time", "temp"] as const;
export const OFFBOARDING_TRIGGERS = [
  "resignation",
  "termination",
  "end-of-contract",
  "retirement",
  "leave-not-returned",
  "temp-not-converted",
] as const;

/* ----------------------------------------------------------------- onboarding -- */

const onboardingNodes: TemplateNode[] = [
  {
    key: "offer-confirmation",
    kind: "task",
    title: "Send offer confirmation and start date",
    department: "HR",
    deadline: { from: "anchor", days: -10 },
    fields: [
      {
        key: "account-tier",
        label: "Account tier",
        options: ["Full", "Kiosk / shared device"],
        required: true,
      },
    ],
  },
  {
    key: "background-initiated",
    kind: "task",
    title: "Background check initiated",
    department: "HR",
    deadline: { from: "anchor", days: -7 },
  },
  {
    key: "equipment-type",
    kind: "task",
    title: "Equipment type determined",
    department: "Management",
    deadline: { from: "anchor", days: -7 },
    fields: [
      {
        key: "equipment",
        label: "Equipment",
        options: ["Laptop", "Desktop", "Shared terminal", "None"],
        required: true,
      },
    ],
  },
  {
    key: "background-cleared",
    kind: "task",
    title: "Background check cleared",
    department: "HR",
    deadline: { from: "anchor", days: -5 },
    compliance: true,
  },
  {
    key: "nda",
    kind: "task",
    title: "NDA executed",
    department: "HR",
    deadline: { from: "anchor", days: -5 },
    conditions: [onlyFor("contractor")],
    compliance: true,
  },
  {
    key: "exec-package",
    kind: "task",
    title: "Executive onboarding package prepared",
    department: "HR",
    deadline: { from: "anchor", days: -5 },
    conditions: [onlyFor("executive")],
  },
  {
    key: "it-order",
    kind: "task",
    title: "IT provisioning order placed",
    department: "IT",
    deadline: { from: "anchor", days: -5 },
    inputs: [
      { node: "equipment-type", field: "equipment" },
      { node: "offer-confirmation", field: "account-tier" },
    ],
  },
  {
    key: "home-office-shipment",
    kind: "task",
    title: "Home office equipment shipped",
    department: "IT",
    deadline: { from: "anchor", days: -3 },
    conditions: [onlyFor("remote")],
    fields: [{ key: "tracking", label: "Tracking link or number" }],
  },
  {
    key: "workstation",
    kind: "task",
    title: "Office space / workstation prepared",
    department: "Facilities",
    fields: [{ key: "keys-issued", label: "Keys issued (count)", issues: "keys" }],
    deadline: { from: "anchor", days: -3 },
    conditions: [notFor("remote")],
  },
  {
    key: "badge",
    kind: "task",
    title: "Badge and access card ordered",
    department: "Facilities",
    signoffs: ["IT"],
    fields: [{ key: "badge-number", label: "Badge number", issues: "badge" }],
    deadline: { from: "anchor", days: -3 },
    conditions: [notFor("remote")],
  },
  {
    key: "ea-assignment",
    kind: "task",
    title: "Executive assistant assigned",
    department: "Management",
    deadline: { from: "anchor", days: -3 },
    conditions: [onlyFor("executive")],
  },
  {
    key: "accounts",
    kind: "task",
    title: "System accounts created",
    department: "IT",
    deadline: { from: "anchor", days: -1 },
    inputs: [{ node: "offer-confirmation", field: "account-tier" }],
  },
  {
    key: "vpn",
    kind: "task",
    title: "VPN and remote access configured",
    department: "IT",
    deadline: { from: "anchor", days: -1 },
    conditions: [onlyFor("remote")],
  },
  {
    key: "accounts-ready",
    kind: "notify",
    title: "Accounts are ready: send the welcome email",
    department: "Management",
    deadline: { from: "anchor", days: -1 },
  },
  {
    key: "welcome-email",
    kind: "task",
    title: "Welcome email sent with Day 1 logistics",
    department: "Management",
    deadline: { from: "anchor", days: -1 },
  },
  {
    key: "i9",
    kind: "task",
    title: "I-9 verification completed",
    description:
      "Section 1 is the employee's, by the end of their first day. Section 2 is yours, within 3 business days after the first day, with the original documents in person. Keep the form 3 years after the hire or 1 year after the job ends, whichever is later. Guidance from USCIS, not legal advice.",
    department: "HR",
    deadline: { from: "anchor", days: 3, unit: "business" },
    compliance: true,
    fields: [{ key: "i9-reference", label: "I-9 document reference", pii: true, required: true }],
  },
  {
    key: "device-issued",
    kind: "task",
    title: "Device issued and configured",
    department: "IT",
    fields: [{ key: "device-serial", label: "Device serial number", issues: "device" }],
    deadline: { from: "anchor", days: 0 },
    conditions: [notFor("remote")],
    inputs: [{ node: "equipment-type", field: "equipment" }],
  },
  {
    key: "orientation",
    kind: "task",
    title: "Mandatory compliance orientation",
    description: "HIPAA, safety, and anything else this site is required to cover on the first day.",
    department: "Safety",
    deadline: { from: "anchor", days: 0 },
    compliance: true,
  },
  {
    key: "ppe",
    kind: "task",
    title: "PPE issued",
    department: "Safety",
    deadline: { from: "anchor", days: 0 },
    conditions: [onlyFor("warehouse")],
  },
  {
    key: "benefits-start",
    kind: "task",
    title: "Benefits enrollment initiated",
    department: "HR",
    deadline: { from: "anchor", days: 0 },
    conditions: [notFor("contractor"), { field: "employmentStatus", op: "notIn", values: ["temp"] }],
  },
  {
    key: "role-access",
    kind: "task",
    title: "Role-specific system access granted",
    department: "IT",
    signoffs: ["Management"],
    deadline: { from: "anchor", days: 3 },
  },
  {
    key: "legal-briefing",
    kind: "task",
    title: "Legal briefings",
    department: "Legal",
    deadline: { from: "anchor", days: 5 },
    conditions: [onlyFor("executive")],
  },
  {
    key: "forklift",
    kind: "task",
    title: "Forklift certification",
    department: "Safety",
    deadline: { from: "anchor", days: 5 },
    conditions: [onlyFor("warehouse")],
    compliance: true,
  },
  {
    key: "warehouse-access",
    kind: "task",
    title: "Warehouse access card activated",
    department: "Facilities",
    deadline: { from: "anchor", days: 5 },
    conditions: [onlyFor("warehouse")],
  },
  {
    key: "benefits-deadline",
    kind: "task",
    title: "Benefits enrollment completed",
    department: "HR",
    deadline: { from: "anchor", days: 10 },
    conditions: [notFor("contractor"), { field: "employmentStatus", op: "notIn", values: ["temp"] }],
  },
  {
    key: "stipend",
    kind: "task",
    title: "Home office stipend processed",
    department: "HR",
    deadline: { from: "anchor", days: 10 },
    conditions: [onlyFor("remote")],
  },
  {
    key: "day-30",
    kind: "task",
    title: "30-day check-in",
    description:
      "For a temp, record the decision here, then act on it from their page in Employees: convert, extend the contract, or begin an offboarding.",
    department: "HR",
    signoffs: ["Management"],
    deadline: { from: "anchor", days: 30 },
    // A week of working days to arrange it, rather than a month in HR's "Now".
    notBefore: { from: "anchor", days: 25 },
    fields: [
      {
        key: "decision",
        label: "Temp-to-perm decision",
        options: ["Convert", "Extend", "Begin offboarding", "Not applicable"],
      },
    ],
  },
];

export const STANDARD_ONBOARDING: Template = {
  id: "starter-onboarding",
  name: "Standard onboarding",
  // 2: the 30-day check-in opens on day 25 rather than on the first day.
  version: 2,
  kind: "onboarding",
  nodes: onboardingNodes,
  edges: [
    ...hard("offer-confirmation", "background-initiated", "workstation", "nda"),
    ...hard("background-initiated", "background-cleared"),
    ...hard("background-cleared", "it-order", "badge"),
    ...hard("equipment-type", "it-order"),
    ...hard("nda", "it-order"),
    ...hard("it-order", "accounts", "home-office-shipment"),
    ...hard("accounts", "accounts-ready", "vpn", "device-issued"),
    ...hard("accounts-ready", "welcome-email"),
    ...hard("i9", "benefits-start", "role-access"),
    ...hard("device-issued", "role-access"),
    ...hard("vpn", "role-access"),
    ...hard("orientation", "role-access", "forklift"),
    ...hard("forklift", "warehouse-access"),
    ...hard("benefits-start", "benefits-deadline"),
    ...hard("home-office-shipment", "stipend"),
    ...soft("exec-package", "legal-briefing"),
  ],
};

/* ---------------------------------------------------------------- offboarding -- */

const offboardingNodes: TemplateNode[] = [
  {
    key: "departure-confirmed",
    kind: "task",
    title: "Departure confirmed; last day set",
    department: "HR",
    deadline: { from: "trigger", days: 0 },
    timeCritical: true,
  },
  {
    key: "tell-it",
    kind: "notify",
    title: "IT: an employee is leaving",
    department: "IT",
    deadline: { from: "trigger", days: 0 },
  },
  {
    key: "tell-facilities",
    kind: "notify",
    title: "Facilities: an employee is leaving",
    department: "Facilities",
    deadline: { from: "trigger", days: 0 },
  },
  // PRD 9.4: inventory, then notes per domain, then sessions, then a review
  // that is a soft dependency before final payroll.
  {
    key: "kt-plan",
    kind: "task",
    title: "Knowledge inventory discussion held",
    description:
      "Manager and leaver together list the key processes, systems and relationships. Each becomes a note on the next card.",
    department: "Management",
    deadline: { from: "trigger", days: 1 },
    conditions: [triggerIsNot("end-of-contract", "leave-not-returned")],
  },
  {
    key: "kt-notes",
    kind: "task",
    title: "Handover notes written, one per area",
    description: "One note for each area the inventory named, with a link to any document the leaver wrote.",
    department: "Management",
    deadline: { from: "anchor", days: -5 },
    conditions: [triggerIsNot("end-of-contract", "leave-not-returned")],
    notes: true,
  },
  {
    key: "backfill",
    kind: "task",
    title: "Backfill request submitted (if applicable)",
    department: "Management",
    deadline: { from: "anchor", days: -14 },
    conditions: [triggerIsNot("leave-not-returned")],
  },
  {
    key: "hardware-scheduled",
    kind: "task",
    title: "Hardware and equipment return scheduled",
    department: "IT",
    deadline: { from: "anchor", days: -7 },
  },
  {
    key: "access-audit",
    kind: "task",
    title: "Access audit: every credential listed for deactivation",
    department: "IT",
    deadline: { from: "anchor", days: -7 },
    fields: [{ key: "systems", label: "Systems to deactivate", required: true }],
  },
  {
    key: "benefits-communicated",
    kind: "task",
    title: "Benefits termination date communicated",
    department: "HR",
    deadline: { from: "anchor", days: -5 },
    conditions: [notFor("contractor"), triggerIsNot("leave-not-returned")],
  },
  {
    key: "exit-scheduled",
    kind: "task",
    title: "Exit interview scheduled",
    department: "HR",
    deadline: { from: "anchor", days: -3 },
    conditions: [triggerIs("resignation", "retirement")],
  },
  {
    key: "recognition",
    kind: "task",
    title: "Recognition for retirement",
    department: "Management",
    deadline: { from: "anchor", days: -1 },
    conditions: [triggerIs("retirement")],
  },
  {
    key: "kt-sessions",
    kind: "task",
    title: "Handoff sessions held",
    description: "One note per session: who attended, what was covered, what is left.",
    department: "Management",
    deadline: { from: "anchor", days: -2 },
    conditions: [triggerIsNot("end-of-contract", "leave-not-returned")],
    notes: true,
  },
  {
    key: "kt-review",
    kind: "task",
    title: "Knowledge transfer reviewed: manager is satisfied",
    department: "Management",
    deadline: { from: "anchor", days: -1 },
    conditions: [triggerIsNot("end-of-contract", "leave-not-returned")],
  },
  {
    key: "exit-interview",
    kind: "task",
    title: "Exit interview conducted",
    department: "HR",
    deadline: { from: "anchor", days: 0 },
    conditions: [triggerIs("resignation", "retirement")],
  },
  {
    key: "hardware-retrieved",
    kind: "task",
    title: "Hardware retrieved (laptop, phone, badge, peripherals)",
    department: "IT",
    fields: [
      { key: "device-returned", label: "Device serial number returned", returns: "device" },
      { key: "badge-returned", label: "Badge number returned", returns: "badge" },
    ],
    signoffs: ["Facilities"],
    deadline: { from: "anchor", days: 0, unit: "calendar" },
    timeCritical: true,
  },
  {
    key: "access-deactivated",
    kind: "task",
    title: "All system access deactivated",
    revokesAccess: true,
    description: "SSO, email, tools, VPN. Suspend rather than delete when the person may return.",
    department: "IT",
    deadline: { from: "anchor", days: 0, unit: "calendar" },
    timeCritical: true,
    inputs: [{ node: "access-audit", field: "systems" }],
  },
  {
    key: "building-access",
    kind: "task",
    title: "Building access revoked",
    revokesAccess: true,
    department: "Facilities",
    fields: [{ key: "keys-returned", label: "Keys returned (count)", returns: "keys" }],
    deadline: { from: "anchor", days: 0, unit: "calendar" },
    timeCritical: true,
  },
  {
    key: "final-payroll",
    kind: "task",
    title: "Final payroll confirmed and submitted",
    department: "HR",
    signoffs: ["Finance"],
    finalPay: true,
    deadline: { from: "anchor", days: 0, unit: "calendar" },
    timeCritical: true,
    conditions: [triggerIsNot("leave-not-returned")],
  },
  {
    key: "data-transfer",
    kind: "task",
    title: "Company data transferred; personal data removed",
    department: "IT",
    deadline: { from: "anchor", days: 0, unit: "calendar" },
    timeCritical: true,
  },
  {
    key: "auto-reply",
    kind: "task",
    title: "Email auto-reply and forwarding configured",
    department: "IT",
    deadline: { from: "anchor", days: 1 },
  },
  {
    key: "deactivations-verified",
    kind: "task",
    title: "All deactivations verified by the IT lead",
    department: "IT",
    deadline: { from: "anchor", days: 1 },
    timeCritical: true,
  },
  {
    key: "benefits-processed",
    kind: "task",
    title: "Benefits termination processed with the carrier",
    department: "HR",
    deadline: { from: "anchor", days: 3 },
    conditions: [notFor("contractor"), triggerIsNot("leave-not-returned")],
  },
  {
    key: "cobra-notice",
    kind: "task",
    title: "COBRA: the leaver reported to the plan administrator",
    description: COBRA_DESCRIPTION,
    department: "HR",
    deadline: { from: "anchor", days: 5, unit: "business" },
    conditions: [notFor("contractor")],
  },
  {
    key: "audit-export",
    kind: "task",
    title: "Offboarding audit log exported and filed",
    department: "HR",
    deadline: { from: "anchor", days: 5 },
    compliance: true,
  },
  {
    key: "followup-audit",
    kind: "task",
    title: "Follow-up access audit: no credentials outstanding",
    department: "IT",
    deadline: { from: "anchor", days: 30 },
    compliance: true,
  },
];

export const STANDARD_OFFBOARDING: Template = {
  id: "starter-offboarding",
  name: "Standard offboarding",
  version: 3,
  kind: "offboarding",
  use: "standard",
  nodes: offboardingNodes,
  edges: [
    ...hard(
      "departure-confirmed",
      "tell-it",
      "tell-facilities",
      "kt-plan",
      "benefits-communicated",
      "exit-scheduled",
      "final-payroll",
    ),
    ...hard("tell-it", "hardware-scheduled", "access-audit", "data-transfer"),
    ...hard("tell-facilities", "building-access"),
    ...hard("hardware-scheduled", "hardware-retrieved"),
    ...hard("access-audit", "access-deactivated"),
    ...hard("kt-plan", "kt-notes"),
    ...hard("kt-notes", "kt-sessions"),
    ...hard("kt-sessions", "kt-review"),
    ...hard("exit-scheduled", "exit-interview"),
    ...hard("access-deactivated", "auto-reply", "deactivations-verified"),
    ...hard("building-access", "deactivations-verified"),
    ...hard("hardware-retrieved", "deactivations-verified"),
    ...hard("benefits-communicated", "benefits-processed"),
    ...hard("benefits-processed", "cobra-notice"),
    ...hard("deactivations-verified", "audit-export", "followup-audit"),
    ...hard("final-payroll", "audit-export"),
    // PRD 9.4: the knowledge-transfer review is a soft dependency before final payroll.
    ...soft("kt-review", "final-payroll"),
  ],
};

/* --------------------------------------------------------------- involuntary -- */

export const INVOLUNTARY_OFFBOARDING: Template = {
  id: "starter-involuntary",
  name: "Involuntary termination (high speed)",
  version: 2,
  kind: "offboarding",
  use: "termination",
  nodes: [
    {
      key: "tell-it",
      kind: "notify",
      title: "URGENT: revoke access now",
      department: "IT",
      urgency: "urgent",
      deadline: { from: "trigger", minutes: 5 },
    },
    {
      key: "tell-facilities",
      kind: "notify",
      title: "URGENT: revoke building access now",
      department: "Facilities",
      urgency: "urgent",
      deadline: { from: "trigger", minutes: 5 },
    },
    {
      key: "access-deactivated",
      kind: "task",
      title: "All system access deactivated",
      revokesAccess: true,
      description: "SSO, email, tools, VPN.",
      department: "IT",
      deadline: { from: "trigger", minutes: 30 },
      timeCritical: true,
    },
    {
      key: "building-access",
      kind: "task",
      title: "Building access revoked",
      revokesAccess: true,
      department: "Facilities",
      fields: [{ key: "keys-returned", label: "Keys returned (count)", returns: "keys" }],
      deadline: { from: "trigger", minutes: 15 },
      timeCritical: true,
    },
    {
      key: "hardware-retrieved",
      kind: "task",
      title: "Hardware retrieved",
      department: "IT",
      fields: [
        { key: "device-returned", label: "Device serial number returned", returns: "device" },
        { key: "badge-returned", label: "Badge number returned", returns: "badge" },
      ],
      deadline: { from: "trigger", days: 0, unit: "calendar" },
      timeCritical: true,
    },
    {
      key: "final-payroll",
      kind: "task",
      title: "Final payroll confirmed and submitted",
      description: "Some states require final pay on the day of termination. Check the rule for this site.",
      department: "HR",
      signoffs: ["Finance"],
      finalPay: true,
      deadline: { from: "trigger", days: 0, unit: "calendar" },
      timeCritical: true,
    },
    {
      key: "final-check-ready",
      kind: "task",
      title: "Final paycheck ready to hand over at the meeting",
      department: "HR",
      finalPay: true,
      deadline: { from: "trigger", days: 0, unit: "calendar" },
      timeCritical: true,
      conditions: [{ field: "finalPay", op: "in", values: ["at-meeting"] }],
    },
    {
      key: "cobra-notice",
      kind: "task",
      title: "COBRA: the leaver reported to the plan administrator",
      description: COBRA_DESCRIPTION,
      department: "HR",
      deadline: { from: "trigger", days: 5, unit: "business" },
      conditions: [notFor("contractor")],
    },
    {
      key: "data-transfer",
      kind: "task",
      title: "Company data transferred; personal data removed",
      department: "IT",
      deadline: { from: "trigger", days: 1 },
    },
    {
      key: "exit-form",
      kind: "task",
      title: "Written exit form offered (optional)",
      department: "HR",
      deadline: { from: "trigger", days: 3 },
    },
    {
      key: "deactivations-verified",
      kind: "task",
      title: "All deactivations verified by the IT lead",
      department: "IT",
      deadline: { from: "trigger", days: 1 },
      timeCritical: true,
    },
    {
      key: "audit-export",
      kind: "task",
      title: "Offboarding audit log exported and filed",
      department: "HR",
      deadline: { from: "trigger", days: 5 },
      compliance: true,
    },
  ],
  edges: [
    ...hard("tell-it", "access-deactivated", "data-transfer"),
    ...hard("tell-facilities", "building-access"),
    ...hard("access-deactivated", "deactivations-verified"),
    ...hard("building-access", "deactivations-verified"),
    ...hard("hardware-retrieved", "deactivations-verified"),
    ...hard("deactivations-verified", "audit-export"),
    ...hard("final-payroll", "audit-export"),
    ...hard("final-check-ready", "final-payroll"),
    ...hard("final-payroll", "cobra-notice"),
  ],
};

/* -------------------------------------------------------------- access change -- */

/**
 * Somebody stays and only some of their access goes: a move to a role that
 * no longer needs a system, a contract that lost a clearance, a project
 * ended (a client's offboarding form, "employee staying"). The manager lists
 * what goes; IT and Facilities take exactly that away on the day; the IT lead
 * verifies it; the manager confirms nothing they still need went with it.
 * None of HR's separation cards -- payroll, benefits, the exit interview,
 * knowledge transfer -- belong here, which is the whole reason it is not the
 * standard offboarding with cards skipped.
 */
export const ACCESS_CHANGE_TEMPLATE: Template = {
  id: "starter-access-change",
  name: "Access change (employee staying)",
  version: 1,
  kind: "offboarding",
  use: "access-change",
  nodes: [
    {
      key: "change-listed",
      kind: "task",
      title: "Access to remove listed",
      description:
        "Every system, area and piece of equipment that goes, and why. They are staying, so anything not listed stays.",
      department: "Management",
      deadline: { from: "trigger", days: 0 },
      fields: [{ key: "systems", label: "Access to remove", required: true }],
    },
    {
      key: "tell-it",
      kind: "notify",
      title: "IT: some of an employee's access is being removed",
      department: "IT",
      deadline: { from: "trigger", days: 0 },
    },
    {
      key: "tell-facilities",
      kind: "notify",
      title: "Facilities: an employee's building access may change",
      department: "Facilities",
      deadline: { from: "trigger", days: 0 },
    },
    {
      key: "access-removed",
      kind: "task",
      title: "Listed system access removed",
      revokesAccess: true,
      description: "Only what the list names. Their other accounts stay as they are.",
      department: "IT",
      deadline: { from: "anchor", days: 0, unit: "calendar" },
      timeCritical: true,
      inputs: [{ node: "change-listed", field: "systems" }],
    },
    {
      key: "building-access",
      kind: "task",
      title: "Building or area access changed (if applicable)",
      revokesAccess: true,
      department: "Facilities",
      deadline: { from: "anchor", days: 0, unit: "calendar" },
      timeCritical: true,
      inputs: [{ node: "change-listed", field: "systems" }],
    },
    {
      key: "equipment-returned",
      kind: "task",
      title: "Equipment no longer needed returned (if applicable)",
      department: "IT",
      deadline: { from: "anchor", days: 3 },
    },
    {
      key: "change-verified",
      kind: "task",
      title: "Access changes verified by the IT lead",
      department: "IT",
      deadline: { from: "anchor", days: 1 },
      timeCritical: true,
    },
    {
      key: "still-works",
      kind: "task",
      title: "Manager confirms they can still do their job",
      description: "Nothing they still need went with the change.",
      department: "Management",
      deadline: { from: "anchor", days: 2 },
    },
    {
      key: "followup-audit",
      kind: "task",
      title: "Follow-up access audit: removed access stays removed",
      department: "IT",
      deadline: { from: "anchor", days: 30 },
      compliance: true,
    },
  ],
  edges: [
    ...hard("change-listed", "tell-it", "tell-facilities"),
    ...hard("tell-it", "access-removed", "equipment-returned"),
    ...hard("tell-facilities", "building-access"),
    ...hard("access-removed", "change-verified"),
    ...hard("building-access", "change-verified"),
    ...hard("change-verified", "still-works", "followup-audit"),
  ],
};

export const STARTER_TEMPLATES: readonly Template[] = [
  STANDARD_ONBOARDING,
  STANDARD_OFFBOARDING,
  INVOLUNTARY_OFFBOARDING,
  ACCESS_CHANGE_TEMPLATE,
];
